import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { fetchRoutePlans, fetchRoutePlanCandidates, fetchInvoicesByDueRange } from '../api'

const sanitizeArray = (value) =>
  Array.isArray(value) ? value.filter(Boolean) : value ? [value] : []

export const useRoutePlans = ({ from, to, status, enabled = true } = {}) => {
  const statusList = useMemo(() => sanitizeArray(status), [status])
  const filters = useMemo(
    () => ({
      from: from || '',
      to: to || '',
      status: statusList,
    }),
    [from || '', to || '', statusList.join(',')]
  )

  return useQuery({
    queryKey: ['routePlans', filters],
    queryFn: async () => {
      const response = await fetchRoutePlans({
        from: filters.from || undefined,
        to: filters.to || undefined,
        status: filters.status,
      })
      return response.data?.data || []
    },
    keepPreviousData: true,
    enabled,
  })
}

export const useRoutePlanCandidates = ({
  from,
  to,
  statusIn,
  limit = 200,
  enabled = true,
} = {}) => {
  const statusInList = useMemo(() => sanitizeArray(statusIn), [statusIn])
  const filters = useMemo(
    () => ({
      from: from || '',
      to: to || '',
      limit,
      statusIn: statusInList,
    }),
    [from || '', to || '', limit, statusInList.join(',')]
  )

  return useQuery({
    queryKey: ['routePlanCandidates', filters],
    queryFn: async () => {
      const response = await fetchRoutePlanCandidates({
        from: filters.from || undefined,
        to: filters.to || undefined,
        limit: filters.limit,
        statusIn: filters.statusIn,
      })
      return response.data?.data || []
    },
    enabled,
    keepPreviousData: true,
    staleTime: 60 * 1000,
  })
}

export const useInvoicesDueRange = ({ from, to, enabled = true } = {}) => {
  const filters = useMemo(
    () => ({ from: from || '', to: to || '' }),
    [from || '', to || '']
  )

  return useQuery({
    queryKey: ['invoicesDueRange', filters],
    queryFn: async () => {
      const response = await fetchInvoicesByDueRange({
        from: filters.from || undefined,
        to: filters.to || undefined,
      })
      return response.data?.data || []
    },
    keepPreviousData: true,
    enabled: Boolean(filters.from || filters.to) && enabled,
    staleTime: 60 * 1000,
  })
}
